<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { EntryFeeTierOperatorEnum } from '@/enums/entryFeeTierOperator_enum.ts';
import { MAX_ENTRY_FEE_TIERS, getDuplicateEntryFeeTierIndexes, isEntryFeeTierValid } from '@/utils/entryFeeTiers.ts';

import type { EntryFeeTier } from '@/types/services/mandate/mandate.type.ts';

const { t } = useI18n();

const props = defineProps<{
  modelValue?: EntryFeeTier[]
  disabled?: boolean
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: EntryFeeTier[]): void
}>();

const tiers = computed<EntryFeeTier[]>(() => props.modelValue ?? []);

const operators = Object.values(EntryFeeTierOperatorEnum).map(operator => ({
  title: operator,
  value: operator
}));

const canAdd = computed<boolean>(() => !props.disabled && tiers.value.length < MAX_ENTRY_FEE_TIERS);

const amountRules = [
  (value: number | string | null) => (value !== null && value !== '' && Number(value) > 0) || t('components.entryFeeTiers.amountRule')
];
const rateRules = [
  (value: number | string | null) => (value !== null && value !== '' && Number(value) >= 0 && Number(value) <= 100) || t('components.entryFeeTiers.rateRule')
];

function emitTiers(tiers: EntryFeeTier[]): void {
  emit('update:modelValue', tiers);
}

function updateTier(index: number, patch: Partial<EntryFeeTier>): void {
  emitTiers(tiers.value.map((tier, i) => i === index ? { ...tier, ...patch } : tier));
}

function toNumberOrNull(value: string | number | null): number | null {
  return value === '' || value === null ? null : Number(value);
}

function addTier(): void {
  emitTiers([...tiers.value, { operator: EntryFeeTierOperatorEnum.GTE, amount: null, feeRate: null }]);
}

function removeTier(index: number): void {
  emitTiers(tiers.value.filter((_, i) => i !== index));
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value);
}

const duplicateIndexes = computed(() => getDuplicateEntryFeeTierIndexes(tiers.value));

function amountErrors(index: number): string[] {
  return duplicateIndexes.value.has(index) ? [t('components.entryFeeTiers.duplicate')] : [];
}

const preview = computed<string[]>(() =>
  tiers.value
    .filter(isEntryFeeTierValid)
    .map(tier => t('components.entryFeeTiers.previewLine', {
      operator: tier.operator,
      amount: formatNumber(Number(tier.amount)),
      rate: formatNumber(Number(tier.feeRate))
    }))
);
</script>

<template>
  <div>
    <p class="text-label font-weight-bold mb-1">{{ t('components.entryFeeTiers.title') }}</p>
    <p class="text-caption mb-3">{{ t('components.entryFeeTiers.help') }}</p>

    <p v-if="tiers.length === 0" class="text-caption mb-3">{{ t('components.entryFeeTiers.empty') }}</p>

    <v-row
      v-for="(tier, index) in tiers"
      :key="index"
      align="start"
      class="mb-1"
      dense
    >
      <v-col cols="12" sm="2">
        <v-select
          :model-value="tier.operator"
          :disabled="disabled"
          :items="operators"
          :label="t('components.entryFeeTiers.operator')"
          density="compact"
          hide-details="auto"
          @update:model-value="updateTier(index, { operator: $event })"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          :model-value="tier.amount"
          :disabled="disabled"
          :label="t('components.entryFeeTiers.amount')"
          :rules="amountRules"
          :error-messages="amountErrors(index)"
          density="compact"
          hide-details="auto"
          min="0"
          suffix="€"
          type="number"
          @update:model-value="updateTier(index, { amount: toNumberOrNull($event) })"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          :model-value="tier.feeRate"
          :disabled="disabled"
          :label="t('components.entryFeeTiers.rate')"
          :rules="rateRules"
          density="compact"
          hide-details="auto"
          min="0"
          max="100"
          suffix="%"
          type="number"
          @update:model-value="updateTier(index, { feeRate: toNumberOrNull($event) })"
        />
      </v-col>
      <v-col cols="12" sm="2" class="d-flex align-center">
        <v-btn
          :disabled="disabled"
          :aria-label="t('components.entryFeeTiers.remove')"
          color="error"
          icon="PhTrash"
          size="small"
          variant="text"
          @click.prevent="removeTier(index)"
        />
      </v-col>
    </v-row>

    <v-btn
      :disabled="!canAdd"
      class="mt-2"
      size="small"
      variant="outlined"
      @click.prevent="addTier"
    >
      {{ t('components.entryFeeTiers.add') }}
      <template #prepend><v-icon icon="PhPlus" /></template>
    </v-btn>
    <span v-if="tiers.length >= MAX_ENTRY_FEE_TIERS" class="text-caption ml-2">
      {{ t('components.entryFeeTiers.max', { max: MAX_ENTRY_FEE_TIERS }) }}
    </span>

    <div v-if="preview.length > 0" class="mt-3">
      <p class="text-caption font-weight-bold">{{ t('components.entryFeeTiers.preview') }}</p>
      <p v-for="(line, index) in preview" :key="index" class="text-caption">{{ line }}</p>
      <p class="text-caption">{{ t('components.entryFeeTiers.previewFallback') }}</p>
    </div>
  </div>
</template>
